import { useState } from 'react';
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react';
import { liteApi } from '@/utils/lite/liteApi';

/**
 * Anonymous thumbs up/down widget for PlayFirst Lite tool outputs.
 *
 * Privacy: no inputs or outputs are sent to the server — only the tool name,
 * rating (up/down), and an optional one-tap reason. Stateless per our Lite
 * privacy commitments. Uses sessionStorage to prevent duplicate submits for
 * the same output in the same browser session.
 *
 * Usage:
 *   <LiteFeedback tool="session-bridge" outputKey={output.slice(0,120)} />
 *
 * `outputKey` should be a short hash/slice of the output that changes when
 * the user regenerates — so they can rate each output once.
 */
const DOWN_REASONS = [
  { value: 'hallucination', label: 'Made things up' },
  { value: 'tone', label: 'Wrong tone' },
  { value: 'formatting', label: 'Formatting' },
  { value: 'not_helpful', label: 'Not helpful' },
  { value: 'other', label: 'Other' },
];

const UP_REASONS = [
  { value: 'accurate', label: 'Accurate' },
  { value: 'affirming', label: 'Affirming language' },
  { value: 'great', label: 'Saves me time' },
];

export default function LiteFeedback({ tool, outputKey = '' }) {
  const storageKey = `pf_lite_fb_${tool}_${(outputKey || '').slice(0, 40)}`;
  const [rating, setRating] = useState(() => {
    try { return sessionStorage.getItem(storageKey) || ''; } catch { return ''; }
  });
  const [showReasonPicker, setShowReasonPicker] = useState(false);
  const [submittingReason, setSubmittingReason] = useState(false);
  const [reasonSent, setReasonSent] = useState(false);
  const [error, setError] = useState('');

  const persistRating = (value) => {
    try { sessionStorage.setItem(storageKey, value); } catch {/* ignore */}
  };

  const sendRating = async (value) => {
    if (rating) return; // already rated
    setError('');
    setRating(value);
    persistRating(value);
    setShowReasonPicker(true);
    try {
      await liteApi.feedback({ tool, rating: value });
    } catch (e) {
      // Non-blocking — user experience unaffected by logging failures.
      setError('Could not record feedback.');
    }
  };

  const sendReason = async (reason) => {
    if (reasonSent || submittingReason) return;
    setSubmittingReason(true);
    try {
      await liteApi.feedback({ tool, rating, reason });
      setReasonSent(true);
    } catch {
      setError('Could not record reason.');
    } finally {
      setSubmittingReason(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-stone-100" data-testid={`lite-feedback-${tool}`}>
      {!rating ? (
        <div className="flex items-center gap-2 text-xs text-stone-500">
          <span>Was this helpful?</span>
          <button
            type="button"
            onClick={() => sendRating('up')}
            className="inline-flex items-center gap-1 rounded-full border border-stone-200 px-2.5 py-1 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50 transition"
            data-testid={`lite-feedback-${tool}-up`}
            aria-label="Thumbs up"
          >
            <ThumbsUp className="w-3.5 h-3.5" />
            Yes
          </button>
          <button
            type="button"
            onClick={() => sendRating('down')}
            className="inline-flex items-center gap-1 rounded-full border border-stone-200 px-2.5 py-1 hover:border-red-400 hover:text-red-700 hover:bg-red-50 transition"
            data-testid={`lite-feedback-${tool}-down`}
            aria-label="Thumbs down"
          >
            <ThumbsDown className="w-3.5 h-3.5" />
            No
          </button>
          <span className="ml-2 text-stone-400">Anonymous · no data stored</span>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-emerald-700">
            <Check className="w-3.5 h-3.5" />
            <span>
              Thanks — rated <strong>{rating === 'up' ? '👍' : '👎'}</strong>.
              {showReasonPicker && !reasonSent ? ' Anything more? (optional)' : ''}
            </span>
          </div>
          {showReasonPicker && !reasonSent && (
            <div className="flex flex-wrap gap-1.5" data-testid={`lite-feedback-${tool}-reasons`}>
              {(rating === 'up' ? UP_REASONS : DOWN_REASONS).map((r) => (
                <button
                  key={r.value}
                  type="button"
                  disabled={submittingReason}
                  onClick={() => sendReason(r.value)}
                  className="inline-flex items-center rounded-full bg-stone-50 border border-stone-200 text-xs px-2.5 py-0.5 text-stone-700 hover:bg-stone-100 disabled:opacity-50"
                  data-testid={`lite-feedback-${tool}-reason-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
              <button
                type="button"
                disabled={submittingReason}
                onClick={() => { setShowReasonPicker(false); setReasonSent(true); }}
                className="text-xs text-stone-400 hover:text-stone-600 px-2"
                data-testid={`lite-feedback-${tool}-skip`}
              >
                skip
              </button>
            </div>
          )}
          {reasonSent && (
            <div className="text-xs text-stone-400">Feedback saved — helps us make these tools better.</div>
          )}
          {error && <div className="text-xs text-red-600">{error}</div>}
        </div>
      )}
    </div>
  );
}
